import ReactGA from 'react-ga4';
import posthog from 'posthog-js';

type EventProperties = Record<string, any>;

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  properties?: EventProperties;
}

class Analytics {
  private static isPostHogReady(): boolean {
    return typeof posthog !== 'undefined' && posthog.__loaded;
  }

  static track(event: AnalyticsEvent): void {
    const { category, action, label, value, properties } = event;

    // Send to Google Analytics
    ReactGA.event({
      category,
      action,
      label,
      value,
    });

    // Only send to PostHog if it's properly initialized
    if (this.isPostHogReady()) {
      const eventName = `${category.toLowerCase()}_${action.toLowerCase()}`.replace(/ /g, '_');
      posthog.capture(eventName, {
        category,
        action,
        label,
        value,
        ...properties,
      });
    }
  }
  static trackPageView(path: string): void {
    console.log('PageView: ', path);
    // Send to Google Analytics
    ReactGA.send({ hitType: 'pageview', page: path });

    // Send to PostHog if ready
    if (this.isPostHogReady()) {
      posthog.capture('$pageview', {
        path,
        url: window.location.href,
        referrer: document.referrer,
        title: document.title,
      });
    }
  }

  // Convenience methods for common events
  static trackCalculation(
    calculator:
      | 'Standard'
      | 'Ousia'
      | 'Reverse'
      | 'Semaglutide'
      | 'ZepboundReverse'
      | 'Tirzepatide'
      | 'Vial Transition'
      | 'SplitDose',
    inputs: Record<string, number>
  ) {
    const properties = {
      ...inputs,
      result: Math.round(inputs.result * 100) / 100, // Round to 2 decimal places
      calculator_type: calculator,
    };

    this.track({
      category: 'Calculator',
      action: 'Dose Calculated',
      label: `${calculator} Calculator`,
      properties,
    });
  }

  static trackQuizEvent(quizName: string, action: 'quiz_start' | 'quiz_complete' | 'quiz_restart', score?: number) {
    this.track({
      category: 'Quiz',
      action,
      label: quizName,
      value: score,
      properties: {
        quiz_name: quizName,
        score,
      },
    });
  }

  static trackDisclaimerEvent(action: 'disclaimer_display' | 'disclaimer_accept', duration?: '90Days' | '60Mins') {
    this.track({
      category: 'Disclaimer',
      action,
      label: duration ? `Hide${duration}` : undefined,
      properties: {
        duration,
      },
    });
  }
}

export default Analytics;
